// Er dette punktet i det hele tatt TROVERDIG? — plausibilitetsvurdering av
// innkommende posisjoner, uavhengig av kilde.
//
// HVORFOR DENNE FILEN FINNES (eierfunn 2026-08-17): eieren meldte «det er noe riv
// ruskende galt med posisjonen som GNÅ sender til SKADE» og la ved et skjermbilde
// av COP-en. Sporet sto på 37.78583, -122.40642 — San Francisco. Det var
// iOS-simulatorens standardkoordinat, altså en falsk GPS, og hele kjeden fra
// telefon til hub til kart hadde transportert den helt korrekt.
//
// Feilen var derfor IKKE i transporten. Feilen var at SKADE IKKE SA FRA. Den
// tegnet et vennlig bakkespor 8 000 km utenfor operasjonsområdet, med CEP 5 m og
// «3 min gammel», uten et eneste forbehold — og lot eieren feilsøke vår egen
// kjede for å finne ut at det var utviklingsverktøyet som løy.
//
// DET ER DEN KLASSEN FEIL DENNE MODULEN LUKKER: et situasjonsbilde skal si når
// det ikke tror på seg selv.
//
// ⚠ VI FLAGGER, VI AVVISER IKKE. Det er en bevisst beslutning, og den er ikke
// åpenbar, så her er grunnen: en soldat på øvelse i Tyskland, i transitt over
// Atlanteren eller på et NATO-oppdrag utenfor Norge har en HELT ekte posisjon
// utenfor boksen under. Ville vi avvist den, hadde vi slettet et menneske fra
// situasjonsbildet fordi han var på feil kontinent — nøyaktig den typen stille
// datatap `bevares` i track.ts finnes for å hindre. Et forbehold koster
// ingenting; et bortkastet spor kan koste noen.
//
// ⚠ OG FLAGGET ER ET HINT, IKKE EN DOM. En reell GNÅ-bruker som faktisk står i
// San Francisco blir merket «ser ut som iOS-simulatoren». Det er en akseptert
// falsk positiv: konsekvensen er en linje ekstra i detaljvisningen, mens
// konsekvensen av å ikke merke er at et falskt punkt går inn i bildet som sant.
import { haversineM } from './geo.js';
/**
 * Der en norsk styrke rimeligvis kan befinne seg.
 *
 * BOKSEN ER MED VILJE FOR STOR. Den dekker fastlands-Norge (57,96°N Lindesnes →
 * 71,19°N Knivskjellodden), Svalbard (til 81°N), Jan Mayen (rundt −8°) og
 * Bjørnøya, PLUSS Sverige, Finland, Danmark, Nordsjøen og Barentshavet.
 *
 * Hvorfor ikke en tett Oslofjord-boks, som er GNÅ sitt egentlige område? Fordi
 * flagget da hadde ropt hver gang noen var i Bergen, Bardufoss eller på en
 * Sverige-øvelse — og et varsel som roper på normaltilstanden blir slått av. Vi
 * kan si med sikkerhet at Nord-Amerika er galt. Vi kan ikke si at Trøndelag er
 * galt. Boksen påstår bare det første.
 */
export const NORSK_OPERASJONSOMRAADE = {
    sorLat: 54,
    nordLat: 82,
    vestLon: -12,
    ostLon: 36,
};
/**
 * Koordinater utviklingsverktøy oppgir når det ikke finnes en ekte GPS.
 *
 * Matches på AVSTAND og ikke på likhet, fordi den eksakte konstanten har
 * flyttet seg mellom Xcode-/Android-versjoner (siste desimaler varierer). En
 * radius på 2 km tåler den driften og holder de tre punktene fra hverandre —
 * San Francisco ligger ~50 km fra Cupertino, som ligger ~11 km fra Mountain
 * View, så ingen av dem kan forveksles med en annen.
 */
const KJENTE_KUNSTIGE_PUNKT = [
    // Den vi faktisk ble bitt av. Skjermbildet viste 37.78583, −122.40642.
    { navn: 'iOS-simulatorens standardposisjon (San Francisco)', punkt: { lat: 37.785834, lon: -122.406417 }, radiusM: 2000 },
    // Xcode › Simulator › Features › Location › «Apple» = Apple Park/Infinite Loop.
    { navn: 'Xcode-simulatorens «Apple»-posisjon (Cupertino)', punkt: { lat: 37.3318, lon: -122.0312 }, radiusM: 2000 },
    // Android-emulatorens standard: Googleplex.
    { navn: 'Android-emulatorens standardposisjon (Mountain View)', punkt: { lat: 37.422, lon: -122.084 }, radiusM: 2000 },
];
/** Hvor nær 0,0 et punkt må ligge for å regnes som «uinitialisert». */
const NULLPUNKT_RADIUS_M = 50_000;
const PLAUSIBEL = { plausibel: true };
/**
 * Vurder om et punkt er troverdig for dette området.
 *
 * REKKEFØLGEN PÅ SJEKKENE ER VIKTIG og er valgt etter hvor handlingsbar
 * diagnosen er, ikke etter hvor enkel den er:
 *
 *  1. Kjent kunstig punkt — dette er den ENESTE sjekken som peker rett på
 *     årsaken («du kjører på simulator»). Den må komme først, ellers taper den
 *     mot den generiske «utenfor området» og operatøren må gjette videre.
 *  2. Nullpunktet 0,0 — en GPS uten fiks rapporterer ofte 0,0, som ligger i
 *     Guineabukta. «Uinitialisert GPS» er en helt annen feilretting enn «feil
 *     kontinent», så den fortjener sin egen grunn.
 *  3. Utenfor området — restkategorien.
 */
export function vurderPlausibilitet(p, omraade = NORSK_OPERASJONSOMRAADE) {
    for (const k of KJENTE_KUNSTIGE_PUNKT) {
        if (haversineM(p, k.punkt) <= k.radiusM) {
            return {
                plausibel: false,
                grunn: 'kunstig-punkt',
                merknad: `ser ut som ${k.navn} — sannsynligvis IKKE en ekte GPS`,
            };
        }
    }
    if (haversineM(p, { lat: 0, lon: 0 }) <= NULLPUNKT_RADIUS_M) {
        return {
            plausibel: false,
            grunn: 'nullpunkt',
            merknad: 'ligger på 0°,0° — tyder på GPS uten fiks, ikke på en reell posisjon',
        };
    }
    if (p.lat < omraade.sorLat ||
        p.lat > omraade.nordLat ||
        p.lon < omraade.vestLon ||
        p.lon > omraade.ostLon) {
        return {
            plausibel: false,
            grunn: 'utenfor-omraade',
            merknad: 'langt utenfor operasjonsområdet — kontroller kilden før den brukes',
        };
    }
    return PLAUSIBEL;
}
/**
 * Nøkkelen plausibilitetsforbeholdet får i `Track.extra`.
 *
 * Advarselstrekanten står i NØKKELEN og ikke i verdien med hensikt: COP-ens
 * detaljvisning rendrer nøkkel/verdi-par som de kommer, og en operatør som
 * skummer venstre kolonne skal se at det er noe her uten å lese verdien.
 */
export const PLAUSIBILITET_NOKKEL = '⚠ Posisjon';
//# sourceMappingURL=plausibilitet.js.map